import { Resources } from 'constants/resources';

import Skeleton from '@mui/material/Skeleton';
import { useNormalizeReferences } from 'hooks/useNormalizeReferences';
import { ReferenceArrayInput } from 'react-admin';
import { Alert } from 'shared/mui/Alert/Alert';
import { Stack } from 'shared/mui/Stack/Stack';
import { AutocompleteInput, AutocompleteMultiselectInput } from 'shared/react-admin/Inputs';
import { ReferenceInput } from 'shared/react-admin/ReferenceInputs';
import { User } from 'types/embedded/user';

import { useCopyMerchantAccesses } from '../hooks/useCopyMerchantAccesses';
import { useMerchantAccesses } from '../hooks/useMerchantAccesses';

type Props = {
  user?: User;
};

export const UsersMerchantAccessDialogForm: React.FC<Props> = ({ user }) => {
  const merchantAccessesIds =
    user?.merchantAccesses?.map((merchantAccess) => merchantAccess?.['@id']) || [];
  const { merchantAccesses } = useMerchantAccesses(merchantAccessesIds);

  const { ready } = useNormalizeReferences({
    referenceFields: ['merchantIds'],
    externalRecord: {
      merchantIds: merchantAccesses
        ?.map((m) => m?.merchant?.['@id'] ?? m?.merchant)
        ?.filter(Boolean),
    },
    controllerProps: { resource: Resources.Users, id: user?.id },
    delay: 500,
  });

  useCopyMerchantAccesses();

  return (
    <Stack gap={3}>
      {ready ? (
        <>
          <ReferenceInput
            filter={{ exists: { merchantAccesses: true } }}
            perPage={200}
            reference={Resources.Users}
            source="sourceUser"
          >
            <AutocompleteInput
              filterToQuery={(searchText: string) => ({ fullName: searchText })}
              fullWidth
              helperText={false}
              label="Скопировать доступ от пользователя"
              optionText={(record: User) => record && `${record.firstName} ${record.lastName}`}
              size="small"
              variant="outlined"
            />
          </ReferenceInput>
          <Alert severity="info">
            После копирования доступа текущий список доступных мерчантов будет заменен. Изменения
            сохранятся только после нажатия &quot;Сохранить&quot;.
          </Alert>
          <ReferenceArrayInput perPage={200} reference={Resources.Merchants} source="merchantIds">
            <AutocompleteMultiselectInput
              fullWidth
              helperText={false}
              label="Мерчанты"
              name="merchantIds"
              optionText="name"
              size="small"
            />
          </ReferenceArrayInput>
        </>
      ) : (
        <>
          <Skeleton height={40} variant="rounded" />
          <Skeleton height={72} variant="rounded" />
          <Skeleton height={40} variant="rounded" />
        </>
      )}
    </Stack>
  );
};
